import { useEffect, useState } from "react";
import { Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

export default function AdminCreditFailures() {
  const { t, fmt } = useLanguage();
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);

  const load = () => api.get("/admin/credit/failures").then(({ data }) => setItems(data.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const retry = async (orderId) => {
    setBusyId(orderId);
    try {
      await api.post(`/admin/credit/retry/${orderId}`);
      toast.success(t("fail.retryOk"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setBusyId(null);
    }
  };

  const retryAll = async () => {
    setBusyAll(true);
    try {
      const { data } = await api.post("/admin/credit/retry-all");
      toast.success(`${data.retried} ${t("fail.retryOk")} · ${data.still_failing} ${t("fail.retryFail")}`);
      await load();
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusyAll(false);
    }
  };

  const th = "text-left font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-5 py-3";
  const td = "px-5 py-4 text-sm text-slate-700 align-top";

  return (
    <div data-testid="admin-credit-failures" className="mt-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-2xl text-slate-900">{t("fail.title")}</h2>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">{t("fail.sub")}</p>
        </div>
        {(items || []).length > 0 && (
          <button data-testid="fail-retry-all" onClick={retryAll} disabled={busyAll}
            className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-60">
            {busyAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
            {t("fail.retryAll")}
          </button>
        )}
      </div>

      <div className="mt-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm overflow-x-auto">
        {items === null ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : items.length === 0 ? (
          <p data-testid="fail-empty" className="p-10 text-left text-sm text-slate-400">✓ {t("fail.empty")}</p>
        ) : (
          <table className="w-full min-w-[820px]">
            <thead className="border-b border-slate-100">
              <tr>
                <th className={th}>{t("fail.colOrder")}</th>
                <th className={th}>{t("fail.colUser")}</th>
                <th className={th}>{t("fail.colPackage")}</th>
                <th className={th}>{t("fail.colAmount")}</th>
                <th className={th}>{t("fail.colDate")}</th>
                <th className={th}>{t("fail.colReason")}</th>
                <th className={th}>{t("console.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.order_id} data-testid={`fail-row-${o.order_id}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className={`${td} font-mono-t text-xs`}>{o.order_id}</td>
                  <td className={td}>{o.email || o.user_id}</td>
                  <td className={td}>{o.package_title} <span className="text-slate-400">({o.kind === "topup" ? "ricarica" : "nuova SIM"})</span></td>
                  <td className={`${td} font-semibold`}>{fmt(o.price)}</td>
                  <td className={`${td} text-xs`}>{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className={`${td} text-xs text-red-500 max-w-[220px] truncate`} title={o.failure_reason}>{o.failure_reason || "—"}</td>
                  <td className={td}>
                    <button data-testid={`fail-retry-${o.order_id}`} onClick={() => retry(o.order_id)} disabled={busyId === o.order_id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#FF6B35] text-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-60">
                      {busyId === o.order_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                      {t("fail.retry")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
